import { cloneDeep } from 'lodash';
import { getFramework, getTrackFunc } from '@/framework';
import { DEFAULT_FONT_FAMILY, IChartLayer, IExportPlugin, LayerType, MIN_FONT_SIZE, PluginType } from '@/version-types';
import { Layer } from '../layer';
import { DEFAULT_FONT_COLOR } from '../text-layer/constants';
import { ActiveChartInfo, getChartActions, resetChartLayerMaterialIds } from './actions';
import chartLayerBehavior from './behavior';
import { CHART_LAYER_EVENTS } from './constants/event';
import chartPresets from './presets';
import { getFontsByPosition } from './utils/core';

export class ChartLayer extends Layer {
    constructor(frameworkIdentity: string) {
        super(frameworkIdentity);
        this.framework = getFramework(frameworkIdentity);
        this.openChartEditor = this.openChartEditor.bind(this);
        this.onOpenChartEditor = this.onOpenChartEditor.bind(this);
        this.getChartInfo = this.getChartInfo.bind(this);
        this.addChartLayerByPreset = this.addChartLayerByPreset.bind(this);
    }

    activeChartLayerActions = getChartActions(this.framework.identity);

    openChartEditor(layerId: string) {
        const chartLayer = (this.framework.getLayer(layerId) ?? null) as IChartLayer | null;
        if (!chartLayer) return;
        this.framework.events.emit(CHART_LAYER_EVENTS.OPEN_CHART_EDITOR, { layerId });
    }

    onOpenChartEditor = (fn: (args: { layerId: string }) => void) => {
        const listenerId = this.framework.events.on(CHART_LAYER_EVENTS.OPEN_CHART_EDITOR, fn);
        return () => this.framework.events.off(listenerId);
    };

    getChartInfo(layer: IChartLayer): ActiveChartInfo {
        const seriesLength = layer.data.chartData.length ? layer.data.chartData[0].length - 1 : 0;
        const itemLength = layer.data.chartData.length - 1;
        const totalItemLength = layer.data.type.includes('stacked') ? itemLength : seriesLength * itemLength;
        return {
            type: layer.data.type,
            showMap: {
                legend: layer.data.legend.show,
                grid: layer.data.grid?.show ?? false,
                categoryAxisLabel: layer.data.categoryAxisLabel.show,
                valueAxisLabel: layer.data.valueAxisLabel.show,
                seriesLabel: layer.data.series.label.show,
            },
            // 业务将整个图表视为整体来设置文字样式, 所以直接拿textStyle即可
            textStyle: {
                fontFamily: layer.data.legend.textStyle.fontFamily ?? DEFAULT_FONT_FAMILY().name,
                fontSize: layer.data.legend.textStyle.fontSize,
                color: layer.data.legend.textStyle.color ?? DEFAULT_FONT_COLOR,
            },
            legendPosition: layer.data.legend.position,
            itemStyleBorderRadius: layer.data.series.itemStyle?.borderRadius ?? 0,
            // 计算个大概值, 左右边距加起来大概为layer.width的20%
            maxItemStyleBorderRadius: Math.ceil((layer.width * 0.8) / totalItemLength / 2),
            textList: getFontsByPosition(layer.data, 'all')
                .map((f) => f.text)
                .filter(Boolean),
            colors: layer.data.colors,
        };
    }

    async addChartLayerByPreset(presetId: string): Promise<IChartLayer | null> {
        const stageSize = this.framework.getSizeByIndex(this.framework.stage.getActiveStageIndex());

        // 根据画布做缩放处理
        const getScaledTableLayerByStageSize = (
            layer: Omit<IChartLayer, 'top' | 'left'>,
        ): Omit<IChartLayer, 'top' | 'left'> => {
            const scale = (() => {
                const minWidth = 0;
                const minHeight = 0;
                const maxWidth = Math.max(Math.round(stageSize.width * 0.8), minWidth);
                const maxHeight = Math.max(Math.round(stageSize.height * 0.8), minHeight);
                const scale =
                    maxWidth / maxHeight < layer.width / layer.height
                        ? maxWidth / layer.width
                        : maxHeight / layer.height;
                return scale;
            })();

            const newLayer = cloneDeep(layer);
            newLayer.data.categoryAxisLabel.textStyle.fontSize = Math.max(
                Math.round(newLayer.data.categoryAxisLabel.textStyle.fontSize * scale),
                MIN_FONT_SIZE,
            );
            newLayer.data.valueAxisLabel.textStyle.fontSize = Math.max(
                Math.round(newLayer.data.valueAxisLabel.textStyle.fontSize * scale),
                MIN_FONT_SIZE,
            );
            newLayer.data.legend.textStyle.fontSize = Math.max(
                Math.round(newLayer.data.legend.textStyle.fontSize * scale),
                MIN_FONT_SIZE,
            );
            newLayer.data.series.label.textStyle.fontSize = Math.max(
                Math.round(newLayer.data.series.label.textStyle.fontSize * scale),
                MIN_FONT_SIZE,
            );
            if (newLayer.data.series.itemStyle?.borderRadius) {
                newLayer.data.series.itemStyle.borderRadius = Math.ceil(
                    newLayer.data.series.itemStyle.borderRadius * scale,
                );
            }
            newLayer.width = Math.round(newLayer.width * scale);
            newLayer.height = Math.round(newLayer.height * scale);
            return newLayer;
        };

        const chartPreset = chartPresets.find((preset) => preset.id === presetId);
        if (!chartPreset) return null;

        const presetChartLayer = chartPreset.create();
        const chartLayer: IChartLayer = {
            ...getScaledTableLayerByStageSize(presetChartLayer),
            top: 0,
            left: 0,
        };
        const newLayer = cloneDeep(chartLayer as any as IChartLayer);
        resetChartLayerMaterialIds(this.framework.identity, newLayer);
        return this.addByDiagonal({
            layer: newLayer,
            trackFunc: getTrackFunc(this.framework.identity, newLayer, 'add'),
            isWriteHistory: true,
        }) as IChartLayer;
    }
}

/**
 * 图表图层插件
 */
export const chartLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.CHART,
    createInstance({ identity }) {
        return [{ key: 'ChartLayer', value: new ChartLayer(identity) }];
    },
    createBehavior() {
        return chartLayerBehavior;
    },
};
